import { BaseFlexibleReportRequest } from '@features/flexible-reports';
import { GridColDef } from '@mui/x-data-grid';
import { dateFormatters, formatters } from '@utils/formattersV2';
import { StatusChip } from './StatusChip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownToLine,
  faCopy,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router-dom';
import { DeleteButton } from './DeleteButton';
import { DuplicateButton } from './DuplicateButton';
import { DownloadButton } from './DownloadButton';

const dateValueFormatter =
  <
    K extends keyof Pick<
      BaseFlexibleReportRequest,
      'startDate' | 'endDate' | 'createdAt'
    >,
  >() =>
  (value: BaseFlexibleReportRequest[K]) =>
    dateFormatters.shortLocaleString(new Date(value));

export const columns: GridColDef<BaseFlexibleReportRequest>[] = [
  {
    field: 'name',
    headerName: 'Report Name',
    flex: 1,
    renderCell: ({ row }) => {
      const linkTo =
        row.status === 'draft'
          ? `flexible-report-requests/${row.id}`
          : `flexible-reports/${row.id}`;
      return <Link to={linkTo}>{row.name}</Link>;
    },
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    flex: 1,
    valueFormatter: dateValueFormatter<'startDate'>(),
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    flex: 1,
    valueFormatter: dateValueFormatter<'endDate'>(),
  },
  {
    field: 'locationCount',
    headerName: 'Locations',
    flex: 1,
    valueFormatter: (value: BaseFlexibleReportRequest['locationCount']) =>
      formatters.number(value, { full: true }),
  },
  {
    field: 'creator',
    headerName: 'Creator',
    flex: 1,
    valueGetter: (_, row) => `${row.user.firstName} ${row.user.lastName}`,
    sortable: false,
  },
  {
    field: 'createdAt',
    headerName: 'Date Created',
    flex: 1,
    valueFormatter: dateValueFormatter<'createdAt'>(),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    renderCell: ({ row }) => (
      <StatusChip status={row.status} sx={{ minWidth: '80px' }} />
    ),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: '',
    flex: 1,
    getActions: ({ row }) => [
      ...(row.status === 'draft'
        ? [
            <DeleteButton
              key="delete"
              campaignId={row.campaignId}
              flexibleReportRequestId={row.id}
              gridActionsCellItemProps={{
                icon: <FontAwesomeIcon icon={faTrash} size="1x" />,
                label: 'Delete',
                showInMenu: false,
              }}
            />,
          ]
        : []),
      <DuplicateButton
        key="duplicate"
        campaignId={row.campaignId}
        flexibleReportRequestId={row.id}
        gridActionsCellItemProps={{
          icon: <FontAwesomeIcon icon={faCopy} size="1x" />,
          label: 'Duplicate',
          showInMenu: false,
        }}
      />,
      ...(row.status === 'ready'
        ? [
            <DownloadButton
              key="download"
              campaignId={row.campaignId}
              flexibleReportRequestId={row.id}
              gridActionsCellItemProps={{
                icon: <FontAwesomeIcon icon={faArrowDownToLine} size="1x" />,
                label: 'Download',
                showInMenu: false,
              }}
            />,
          ]
        : []),
    ],
  },
];
